import React, { useState, useCallback, useRef } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { Delete, DragIndicator, FileCopy, KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';

// Импортируем компоненты блоков
import TextBlock from './TextBlock';
import HeadingBlock from './HeadingBlock';
import ImageBlock from './ImageBlock';
import ButtonBlock from './ButtonBlock';
import DividerBlock from './DividerBlock';
import SpacerBlock from './SpacerBlock';
import ListBlock from './ListBlock';
import TestimonialBlock from './TestimonialBlock';
import ProductBlock from './ProductBlock';
import EventBlock from './EventBlock';
import SocialIconsBlock from './SocialIconsBlock';
import FooterBlock from './FooterBlock';
import UnsubscribeBlock from './UnsubscribeBlock';
import ContactUsBlock from './ContactUsBlock';
import HeroBlock from './HeroBlock';

// Импортируем утилитарные функции
import { duplicateBlockWithUniqueIds } from '../../utils/blockUtils';

const ColumnsBlock = ({ data, isSelected, onUpdate, previewMode, onAddToColumn, onSelectBlock, onUpdateBlock, onDeleteBlock, blockId }) => {
  // Логируем только при значимых изменениях (не на каждом рендере)
  const shouldLog = isSelected || (data?.leftContent?.length > 0) || (data?.rightContent?.length > 0);
  if (shouldLog) {
  console.log('🔄 ColumnsBlock RENDER:', {
    blockId,
    leftContentLength: data?.leftContent?.length || 0,
    rightContentLength: data?.rightContent?.length || 0,
      isSelected,
    timestamp: Date.now()
  });
  }

  const [dragOverColumn, setDragOverColumn] = useState(null);
  
  // Throttling для drag events
  const dragThrottleRef = useRef(null);
  const lastDragOverColumn = useRef(null);
  
  // Динамический импорт для избежания циклических зависимостей
  const getBlockComponent = useCallback((blockType) => {
    const basicComponents = {
      text: TextBlock,
      heading: HeadingBlock,
      image: ImageBlock,
      button: ButtonBlock,
      divider: DividerBlock,
      spacer: SpacerBlock,
      list: ListBlock,
      testimonial: TestimonialBlock,
      product: ProductBlock,
      event: EventBlock,
      'social-icons': SocialIconsBlock,
      footer: FooterBlock,
      unsubscribe: UnsubscribeBlock,
      contactUs: ContactUsBlock,
      hero: HeroBlock,
    };
    
    // Для columns и container используем lazy loading
    if (blockType === 'columns' || blockType === 'columns-3') {
      return ColumnsBlock;
    }
    
    if (blockType === 'container') {
      const ContainerBlock = require('./ContainerBlock').default;
      return ContainerBlock;
    }
    
    return basicComponents[blockType];
  }, []);
  
  const {
    columns = 2,
    columnGap = 20,
    leftWidth = '50%',
    rightWidth = '50%',
    columnWidths = ['33.33%', '33.33%', '33.33%'],
    verticalAlign = 'top',
    leftContent = [],
    rightContent = [],
    content = [[], [], []],
    marginTop = 15,
    marginBottom = 15,
    padding = 10,
    backgroundColor = 'transparent',
    opacity = 1,
    boxShadow = 'none',
    backgroundImage = '',
    backgroundSize = 'cover',
    backgroundPosition = 'center',
    borderRadius = 0,
  } = data;

  const containerStyles = {
    marginTop: `${marginTop}px`,
    marginBottom: `${marginBottom}px`,
    padding: `${padding}px`,
    border: isSelected ? '1px dashed #007bff' : 'none',
    borderRadius: isSelected ? '4px' : `${borderRadius}px`,
    backgroundColor,
    backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
    backgroundSize,
    backgroundPosition,
    backgroundRepeat: 'no-repeat',
    opacity,
    boxShadow: boxShadow !== 'none' ? boxShadow : 'none',
  };

  const getColumnStyle = (index) => {
    const gap = previewMode === 'mobile' ? 10 : columnGap;
    
    if (columns === 2) {
      return {
        width: index === 0 ? leftWidth : rightWidth,
        paddingRight: index === 0 ? `${gap / 2}px` : 0,
        paddingLeft: index === 1 ? `${gap / 2}px` : 0,
        verticalAlign,
        display: previewMode === 'mobile' ? 'block' : 'inline-block',
        marginBottom: previewMode === 'mobile' ? '15px' : 0,
      };
    } else {
      return {
        width: previewMode === 'mobile' ? '100%' : (columnWidths[index] || '33.33%'),
        paddingLeft: index > 0 ? `${gap / (columns - 1)}px` : 0,
        paddingRight: index < columns - 1 ? `${gap / (columns - 1)}px` : 0,
        verticalAlign,
        display: previewMode === 'mobile' ? 'block' : 'inline-block',
        marginBottom: previewMode === 'mobile' ? '15px' : 0,
      };
    }
  };

  // Оптимизированные обработчики drag-and-drop
  const handleColumnDragOver = useCallback((e, columnIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Throttling - обновляем индекс только если он изменился
    if (lastDragOverColumn.current !== columnIndex) {
      if (dragThrottleRef.current) {
        clearTimeout(dragThrottleRef.current);
      }
      
      dragThrottleRef.current = setTimeout(() => {
        setDragOverColumn(columnIndex);
        lastDragOverColumn.current = columnIndex;
      }, 16); // ~60fps
    }
  }, []);

  const handleColumnDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Проверяем, действительно ли мышь покинула колонку
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      if (dragThrottleRef.current) {
        clearTimeout(dragThrottleRef.current);
      }
      
      dragThrottleRef.current = setTimeout(() => {
        setDragOverColumn(null);
        lastDragOverColumn.current = null;
      }, 16);
    }
  }, []);

  const handleColumnDrop = useCallback((e, columnIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Очищаем throttling
    if (dragThrottleRef.current) {
      clearTimeout(dragThrottleRef.current);
      dragThrottleRef.current = null;
    }
    
    setDragOverColumn(null);
    lastDragOverColumn.current = null;
    
    try {
      const blockData = JSON.parse(e.dataTransfer.getData('application/json'));
      if (blockData && blockData.type && onAddToColumn) {
        // Используем данные блока из canvas или defaultData для новых блоков
        const dataToUse = blockData.fromCanvas ? blockData.data : blockData.defaultData;
        const fromCanvasId = blockData.fromCanvas ? blockData.originalId : null;
        
        onAddToColumn(columnIndex, blockData.type, dataToUse, fromCanvasId);
        
        // Блок успешно добавлен в колонку
      }
    } catch (error) {
      console.error('Error parsing dropped data:', error);
    }
  }, [onAddToColumn]);

  const renderColumnContent = (columnIndex) => {
    const columnContent = columns === 2 
      ? (columnIndex === 0 ? leftContent : rightContent)
      : (content[columnIndex] || []);

    const isDropZone = dragOverColumn === columnIndex;

    if (!columnContent || columnContent.length === 0) {
      return (
        <Box
          sx={{
            minHeight: '80px',
            backgroundColor: isDropZone ? '#e3f2fd' : '#f8f9fa',
            border: isDropZone ? '2px solid #2196f3' : '2px dashed #dee2e6',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: isDropZone ? '#1976d2' : '#6c757d',
            fontSize: '14px',
            textAlign: 'center',
            padding: '20px',
            transition: 'all 0.2s ease',
            willChange: 'background-color, border-color, color',
            backfaceVisibility: 'hidden',
            transform: 'translateZ(0)',
          }}
          onDragOver={(e) => handleColumnDragOver(e, columnIndex)}
          onDragLeave={handleColumnDragLeave}
          onDrop={(e) => handleColumnDrop(e, columnIndex)}
        >
          {isDropZone ? 'Drop block here' : 'Drop content here'}
        </Box>
      );
    }

    // Рендерим полноценные компоненты блоков
    return (
      <Box 
        sx={{ 
          minHeight: '50px', 
          border: isDropZone ? '2px solid #2196f3' : 'none',
          borderRadius: '4px',
          transition: 'border-color 0.2s ease',
          willChange: isDropZone ? 'border-color' : 'auto',
          backfaceVisibility: 'hidden',
          transform: 'translateZ(0)',
        }}
        onDragOver={(e) => handleColumnDragOver(e, columnIndex)}
        onDragLeave={handleColumnDragLeave}
        onDrop={(e) => handleColumnDrop(e, columnIndex)}
      >
        {columnContent.map((item, index) => {
          const BlockComponent = getBlockComponent(item.type);
          
          if (!BlockComponent) {
            console.warn(`Unknown block type in column: ${item.type}`, item);
            return null;
          }

          return (
            <Box
              key={item.id || index}
              sx={{
                position: 'relative',
                mb: 1,
                border: '1px solid transparent',
                borderRadius: '4px',
                cursor: 'pointer',
                '&:hover': {
                  border: '1px solid #007bff',
                  '& .column-block-controls': {
                    opacity: 1,
                  },
                  '& .drag-handle': {
                    opacity: 1,
                  },
                },
              }}
              onClick={(e) => {
                // Не блокируем событие полностью, только если нужно выбрать внутренний блок
                if (onSelectBlock) {
                  e.stopPropagation();
                  // Передаем полную информацию о родителе для правильного обновления
                  onSelectBlock({
                    ...item,
                    parentId: blockId,
                    parentType: data.columns === 2 ? 'columns' : 'columns-3',
                    columnIndex,
                    parentIndex: index
                  });
                }
              }}
            >
              {/* Мини панель управления для блока в колонке */}
              <Box
                className="column-block-controls"
                sx={{
                  position: 'absolute',
                  top: -25,
                  right: 0,
                  height: 20,
                  backgroundColor: '#2c3e50',
                  borderRadius: '4px 4px 0 0',
                  display: 'flex',
                  alignItems: 'center',
                  px: 0.5,
                  opacity: 0,
                  transition: 'opacity 0.2s',
                  zIndex: 100,
                  pointerEvents: 'auto',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <Tooltip title="Move Up">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (index === 0) return; // Уже наверху
                      
                      const updatedData = { ...data };
                      if (columns === 2) {
                        if (columnIndex === 0) {
                          const newLeftContent = [...leftContent];
                          [newLeftContent[index], newLeftContent[index - 1]] = [newLeftContent[index - 1], newLeftContent[index]];
                          updatedData.leftContent = newLeftContent;
                        } else {
                          const newRightContent = [...rightContent];
                          [newRightContent[index], newRightContent[index - 1]] = [newRightContent[index - 1], newRightContent[index]];
                          updatedData.rightContent = newRightContent;
                        }
                      } else {
                        const newContent = [...content];
                        newContent[columnIndex] = [...newContent[columnIndex]];
                        [newContent[columnIndex][index], newContent[columnIndex][index - 1]] = [newContent[columnIndex][index - 1], newContent[columnIndex][index]];
                        updatedData.content = newContent;
                      }
                      onUpdate(updatedData);
                    }}
                    sx={{ color: '#9b59b6', padding: 0.25, fontSize: '12px' }}
                    disabled={index === 0}
                  >
                    <KeyboardArrowUp fontSize="inherit" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Move Down">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      const currentColumnContent = columns === 2 
                        ? (columnIndex === 0 ? leftContent : rightContent)
                        : (content[columnIndex] || []);
                      
                      if (index === currentColumnContent.length - 1) return; // Уже внизу
                      
                      const updatedData = { ...data };
                      if (columns === 2) {
                        if (columnIndex === 0) {
                          const newLeftContent = [...leftContent];
                          [newLeftContent[index], newLeftContent[index + 1]] = [newLeftContent[index + 1], newLeftContent[index]];
                          updatedData.leftContent = newLeftContent;
                        } else {
                          const newRightContent = [...rightContent];
                          [newRightContent[index], newRightContent[index + 1]] = [newRightContent[index + 1], newRightContent[index]];
                          updatedData.rightContent = newRightContent;
                        }
                      } else {
                        const newContent = [...content];
                        newContent[columnIndex] = [...newContent[columnIndex]];
                        [newContent[columnIndex][index], newContent[columnIndex][index + 1]] = [newContent[columnIndex][index + 1], newContent[columnIndex][index]];
                        updatedData.content = newContent;
                      }
                      onUpdate(updatedData);
                    }}
                    sx={{ color: '#9b59b6', padding: 0.25, fontSize: '12px' }}
                    disabled={index === (columns === 2 ? (columnIndex === 0 ? leftContent : rightContent) : (content[columnIndex] || [])).length - 1}
                  >
                    <KeyboardArrowDown fontSize="inherit" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Duplicate">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Дублируем блок в той же колонке с уникальными ID
                      const duplicatedItem = duplicateBlockWithUniqueIds(item);
                      
                      const updatedData = { ...data };
                      if (columns === 2) {
                        if (columnIndex === 0) {
                          const newLeftContent = [...leftContent];
                          newLeftContent.splice(index + 1, 0, duplicatedItem);
                          updatedData.leftContent = newLeftContent;
                        } else {
                          const newRightContent = [...rightContent];
                          newRightContent.splice(index + 1, 0, duplicatedItem);
                          updatedData.rightContent = newRightContent;
                        }
                      } else {
                        const newContent = [...content];
                        newContent[columnIndex] = [...newContent[columnIndex]];
                        newContent[columnIndex].splice(index + 1, 0, duplicatedItem);
                        updatedData.content = newContent;
                      }
                      onUpdate(updatedData);
                    }}
                    sx={{ color: '#3498db', padding: 0.25, fontSize: '12px' }}
                  >
                    <FileCopy fontSize="inherit" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Удаляем блок из колонки
                      const updatedData = { ...data };
                      if (columns === 2) {
                        if (columnIndex === 0) {
                          updatedData.leftContent = leftContent.filter((_, i) => i !== index);
                        } else {
                          updatedData.rightContent = rightContent.filter((_, i) => i !== index);
                        }
                      } else {
                        const newContent = [...content];
                        newContent[columnIndex] = newContent[columnIndex].filter((_, i) => i !== index);
                        updatedData.content = newContent;
                      }
                      onUpdate(updatedData);
                    }}
                    sx={{ color: '#e74c3c', padding: 0.25, fontSize: '12px' }}
                  >
                    <Delete fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Рендерим сам блок */}
              <Box
                sx={{ 
                  position: 'relative',
                  '&:hover .drag-handle': {
                    opacity: 1,
                  }
                }}
              >
                <Box
                  className="drag-handle"
                  sx={{
                    position: 'absolute',
                    left: -20,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'grab',
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    zIndex: 50,
                    backgroundColor: '#007bff',
                    borderRadius: '50%',
                    width: 16,
                    height: 16,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  draggable
                  onDragStart={(e) => {
                    e.stopPropagation();
                    
                    // Создаем данные для перетаскивания элемента из колонки
                    const dragData = {
                      type: item.type,
                      data: item.data,
                      defaultData: item.data,
                      fromColumn: true,
                      sourceColumnIndex: columnIndex,
                      sourceItemIndex: index,
                      sourceBlockId: blockId,
                      originalId: item.id
                    };
                    
                    e.dataTransfer.setData('application/json', JSON.stringify(dragData));
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  onDragEnd={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <DragIndicator sx={{ fontSize: 12, color: 'white' }} />
                </Box>
                
                <BlockComponent
                  data={item.data || {}}
                  isSelected={false}
                  previewMode={previewMode}
                  onUpdate={(newData) => {
                    // Обновляем блок внутри колонки
                    const updatedData = { ...data };
                    if (columns === 2) {
                      if (columnIndex === 0) {
                        const newLeftContent = [...leftContent];
                        newLeftContent[index] = { ...item, data: { ...item.data, ...newData } };
                        updatedData.leftContent = newLeftContent;
                        // Сохраняем содержимое правой колонки
                        updatedData.rightContent = [...rightContent];
                      } else {
                        const newRightContent = [...rightContent];
                        newRightContent[index] = { ...item, data: { ...item.data, ...newData } };
                        updatedData.rightContent = newRightContent;
                        // Сохраняем содержимое левой колонки
                        updatedData.leftContent = [...leftContent];
                      }
                    } else {
                      const newContent = [...content];
                      newContent[columnIndex] = [...newContent[columnIndex]];
                      newContent[columnIndex][index] = { ...item, data: { ...item.data, ...newData } };
                      updatedData.content = newContent;
                    }
                    onUpdate(updatedData);
                  }}
                  blockId={item.id}
                  onSelectBlock={(selectedItem) => {
                    // Создаем объект с информацией о родительском элементе
                    const itemWithParent = {
                      ...selectedItem,
                      parentType: columns === 2 ? 'columns' : 'columns-3',
                      parentId: blockId,
                        parentIndex: index,
                        columnIndex: columnIndex,
                    };
                    onSelectBlock && onSelectBlock(itemWithParent);
                  }}
                  // КРИТИЧНО: передаем onUpdateBlock для работы PropertyPanel с вложенными элементами
                  onUpdateBlock={(nestedBlockId, nestedUpdateData) => {
                    console.log('Updating nested block in columns:', { nestedBlockId, nestedUpdateData });
                    
                    // Если это обновление текущего элемента
                    if (nestedBlockId === item.id) {
                      const updatedData = { ...data };
                      if (columns === 2) {
                        if (columnIndex === 0) {
                          const newLeftContent = [...leftContent];
                          newLeftContent[index] = { 
                            ...item, 
                            data: typeof nestedUpdateData === 'function' 
                              ? nestedUpdateData(item.data) 
                              : { ...item.data, ...nestedUpdateData }
                          };
                          updatedData.leftContent = newLeftContent;
                          // Сохраняем содержимое правой колонки
                          updatedData.rightContent = [...rightContent];
                        } else {
                          const newRightContent = [...rightContent];
                          newRightContent[index] = { 
                            ...item, 
                            data: typeof nestedUpdateData === 'function' 
                              ? nestedUpdateData(item.data) 
                              : { ...item.data, ...nestedUpdateData }
                          };
                          updatedData.rightContent = newRightContent;
                          // Сохраняем содержимое левой колонки
                          updatedData.leftContent = [...leftContent];
                        }
                      } else {
                        const newContent = [...content];
                        newContent[columnIndex] = [...newContent[columnIndex]];
                        newContent[columnIndex][index] = { 
                          ...item, 
                          data: typeof nestedUpdateData === 'function' 
                            ? nestedUpdateData(item.data) 
                            : { ...item.data, ...nestedUpdateData }
                        };
                        updatedData.content = newContent;
                      }
                      onUpdate(updatedData);
                    } else {
                      // Передаем обновление дальше по цепочке
                      if (onUpdateBlock) {
                        onUpdateBlock(nestedBlockId, nestedUpdateData);
                      }
                    }
                  }}
                  onDeleteBlock={onDeleteBlock}
                  // Передаем onAddToColumn для вложенных layout блоков
                  onAddToColumn={(item.type === 'columns' || item.type === 'columns-3' || item.type === 'container') ? 
                    (nestedColumnIndex, nestedBlockType, nestedBlockData, nestedFromCanvasId = null) => {
                      console.log('Adding to nested column:', { nestedColumnIndex, nestedBlockType, nestedFromCanvasId });
                      
                      // Создаем новый блок для вложенной колонки/контейнера
                      const newNestedBlock = {
                        id: Date.now().toString() + '_nested_' + nestedColumnIndex,
                        type: nestedBlockType,
                        data: nestedBlockData || {},
                        position: 0,
                      };
                      
                      // Обновляем вложенный блок
                      const updatedData = { ...data };
                      const currentItem = { ...item };
                      
                      if (currentItem.type === 'columns' || currentItem.type === 'columns-3') {
                        // Обновляем вложенные колонки
                        if (currentItem.data.columns === 2) {
                          if (nestedColumnIndex === 0) {
                            currentItem.data.leftContent = [...(currentItem.data.leftContent || []), newNestedBlock];
                          } else {
                            currentItem.data.rightContent = [...(currentItem.data.rightContent || []), newNestedBlock];
                          }
                        } else {
                          currentItem.data.content = currentItem.data.content || [[], [], []];
                          currentItem.data.content[nestedColumnIndex] = [...(currentItem.data.content[nestedColumnIndex] || []), newNestedBlock];
                        }
                      } else if (currentItem.type === 'container') {
                        // Обновляем вложенный контейнер
                        const exists = (currentItem.data.content || []).some(b => b.id === newNestedBlock.id);
if (!exists) {
  currentItem.data.content = [...(currentItem.data.content || []), newNestedBlock];
}
                      }
                      
                      // Обновляем родительский блок
                      if (columns === 2) {
                        if (columnIndex === 0) {
                          const newLeftContent = [...leftContent];
                          newLeftContent[index] = currentItem;
                          updatedData.leftContent = newLeftContent;
                        } else {
                          const newRightContent = [...rightContent];
                          newRightContent[index] = currentItem;
                          updatedData.rightContent = newRightContent;
                        }
                      } else {
                        const newContent = [...content];
                        newContent[columnIndex] = [...newContent[columnIndex]];
                        newContent[columnIndex][index] = currentItem;
                        updatedData.content = newContent;
                      }
                      
                      onUpdate(updatedData);
                      
                      // Удаляем исходный блок если он был перемещен с canvas
                      if (nestedFromCanvasId && onDeleteBlock) {
                        onDeleteBlock(nestedFromCanvasId);
                      }
                    } : undefined
                  }
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  if (previewMode === 'mobile' || previewMode === 'tablet') {
    // На мобильных и планшетных устройствах колонки располагаются вертикально
    return (
      <Box sx={containerStyles}>
        {Array.from({ length: columns }, (_, index) => (
          <Box key={index} sx={{ marginBottom: '15px' }}>
            {renderColumnContent(index)}
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box
      sx={containerStyles}
    >
      <Box sx={{ display: 'flex', alignItems: verticalAlign === 'top' ? 'flex-start' : verticalAlign === 'bottom' ? 'flex-end' : 'center' }}>
        {Array.from({ length: columns }, (_, index) => (
          <Box key={index} sx={getColumnStyle(index)}>
            {renderColumnContent(index)}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ColumnsBlock; 